import React, { useContext, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  getSchedules,
  clearSuppressionState,
} from "../../../store/suppression/suppressionSlice";
import {
  CloudProviderContext,
  getAccountCloudType,
  shouldShowAzurePlaceholderView,
} from "../../../CloudProviderContext";
import SuppressionFilters from "./components/SuppressionFilters";
import { formatScheduleName, getFrequencyBadgeValue, getSuppressionAccountLabels } from "./utils/scheduleNameFormatter";

const Suppression = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { selectedProvider } = useContext(CloudProviderContext);
  const isAzureView = shouldShowAzurePlaceholderView(selectedProvider);
  const accountLabels = getSuppressionAccountLabels(isAzureView);

  const { schedules, isFetchingSchedules, fetchSchedulesError } = useSelector(
    (state) => state.suppression
  );
  const accounts = useSelector((state) => state.acctList?.accounts);

  // Filter state
  const [selectedAccountNumber, setSelectedAccountNumber] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Modal state for all schedule types
  const [tagBasedModalOpen, setTagBasedModalOpen] = useState(false);
  const [selectedAccountForTagBased, setSelectedAccountForTagBased] = useState(null);
  const [tagBasedSchedules, setTagBasedSchedules] = useState([]);
  
  const [recurringModalOpen, setRecurringModalOpen] = useState(false);
  const [selectedAccountForRecurring, setSelectedAccountForRecurring] = useState(null);
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  
  const [onDemandModalOpen, setOnDemandModalOpen] = useState(false);
  const [selectedAccountForOnDemand, setSelectedAccountForOnDemand] = useState(null);
  const [onDemandSchedules, setOnDemandSchedules] = useState([]);
  
  const [wafrModalOpen, setWafrModalOpen] = useState(false);
  const [selectedAccountForWafr, setSelectedAccountForWafr] = useState(null);
  const [wafrSchedules, setWafrSchedules] = useState([]);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all schedules data and track fetched API pages
  const [allSchedules, setAllSchedules] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1])); // Track which API pages have been fetched

  // Fetch schedules on mount
  useEffect(() => {
    const params = {
      page: 1,
      page_size: API_PAGE_SIZE,
    };
    dispatch(getSchedules(params));
  }, [dispatch]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allSchedules?.items) return;
    
    // Calculate which API page we need based on current UI page and page size
    // We need to account for filtered data, so we check if we have enough raw data
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      const params = {
        page: apiPageNeeded,
        page_size: API_PAGE_SIZE,
      };
      
      dispatch(getSchedules(params));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allSchedules, fetchedApiPages, dispatch]);

  // Update allSchedules when schedules data is received (merge with existing data)
  useEffect(() => {
    if (schedules && !isFetchingSchedules && schedules.items) {
      setAllSchedules((prev) => {
        if (!prev) {
          return schedules;
        }
        // Merge items, avoiding duplicates
        const existingIds = new Set(prev.items.map((s) => s.schedule_id));
        const newItems = schedules.items.filter(
          (s) => !existingIds.has(s.schedule_id)
        );
        return {
          ...schedules,
          items: [...prev.items, ...newItems],
          total: Math.max(prev.total || 0, schedules.total || 0),
        };
      });
    }
  }, [schedules, isFetchingSchedules]);

  // Helper function to check if schedule is WAFR
  const isWAFRSchedule = (schedule) => {
    if (!schedule) return false;
    const scheduleName = schedule.schedule_name || "";
    return scheduleName.includes("WAFR") || scheduleName.includes("wafr") || 
           formatScheduleName(schedule) === "Well Architected Framework Review";
  };

  // Account numbers for the active cloud provider (AWS vs Azure toggle)
  const providerAccountNumbers = useMemo(() => {
    if (!accounts?.length) return new Set();
    return new Set(
      accounts
        .filter(
          (account) =>
            account?.accountNumber &&
            !String(account.accountNumber).includes("INIT") &&
            getAccountCloudType(account) === selectedProvider
        )
        .map((account) => account.accountNumber)
    );
  }, [accounts, selectedProvider]);

  // Drop account filter if it no longer belongs to the selected provider
  useEffect(() => {
    if (
      selectedAccountNumber &&
      !providerAccountNumbers.has(selectedAccountNumber)
    ) {
      setSelectedAccountNumber("");
      setPage(1);
    }
  }, [selectedProvider, providerAccountNumbers, selectedAccountNumber]);

  // Get unique account numbers from schedules for the selected provider only
  const availableAccountNumbers = useMemo(() => {
    if (!allSchedules?.items) return [];
    const accountSet = new Set();
    allSchedules.items.forEach((schedule) => {
      if (
        schedule.account_number &&
        providerAccountNumbers.has(schedule.account_number)
      ) {
        accountSet.add(schedule.account_number);
      }
    });
    return Array.from(accountSet).sort();
  }, [allSchedules, providerAccountNumbers]);

  // Group and categorize schedules by account
  const groupedSchedulesByAccount = useMemo(() => {
    if (!allSchedules?.items) return [];
    
    let filtered = allSchedules.items.filter((schedule) =>
      providerAccountNumbers.has(schedule.account_number)
    );

    // Filter by account number
    if (selectedAccountNumber) {
      filtered = filtered.filter(
        (schedule) => schedule.account_number === selectedAccountNumber
      );
    }


    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((schedule) => {
        const displayName = formatScheduleName(schedule).toLowerCase();
        const accountNumber = (schedule.account_number || "").toLowerCase();
        return displayName.includes(searchLower) || accountNumber.includes(searchLower);
      });
    }

    // Group by account_number
    const accountMap = new Map();
    
    filtered.forEach((schedule) => {
      const accountNum = schedule.account_number;
      if (!accountNum) return;
      
      if (!accountMap.has(accountNum)) {
        accountMap.set(accountNum, {
          account_number: accountNum,
          recurring: [],
          onDemand: [],
          wafr: [],
          tagBased: [],
        });
      }
      
      const accountData = accountMap.get(accountNum);
      
      // Categorize schedule
      if (schedule.resource_scope_type === "tag_based") {
        accountData.tagBased.push(schedule);
      } else if (schedule.resource_scope_type === "full") {
        if (schedule.schedule_type === "recurring") {
          accountData.recurring.push(schedule);
        } else if (schedule.schedule_type === "on_demand") {
          if (isWAFRSchedule(schedule)) {
            accountData.wafr.push(schedule);
          } else {
            accountData.onDemand.push(schedule);
          }
        }
      }
    });
    
    // Convert to array and sort by account number
    const result = Array.from(accountMap.values()).sort((a, b) => 
      (a.account_number || "").localeCompare(b.account_number || "")
    );
    
    // Apply pagination
    const total = result.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = result.slice(startIndex, endIndex);
    
    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [
    allSchedules,
    selectedAccountNumber,
    searchTerm,
    page,
    pageSize,
    providerAccountNumbers,
  ]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle filter changes
  const handleAccountChange = (accountNumber) => {
    setSelectedAccountNumber(accountNumber);
    setPage(1); // Reset to first page when filter changes
  };

  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };

  // Handle pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle view button click - navigate to frameworks page
  const handleViewSchedule = (schedule) => {
    console.log("View schedule clicked:", schedule);
    // Store schedule data in sessionStorage to carry forward
    sessionStorage.setItem("selectedSchedule", JSON.stringify(schedule));
    const path = `/configuration/suppression/frameworks/${schedule.schedule_id}`;
    console.log("Navigating to:", path);
    history.push(path);
  };

  // Handle view suppressions button click
  const handleViewSuppressions = (schedule) => {
    // Store schedule data in sessionStorage
    sessionStorage.setItem("selectedSchedule", JSON.stringify(schedule));
    history.push(`/configuration/suppression/schedules/${schedule.schedule_id}/suppressions`);
  };

  // Handle tag based click
  const handleTagBasedClick = (accountNumber, tagBasedSchedules) => {
    if (tagBasedSchedules.length === 1) {
      // If only one, directly use it - icons will handle it
      return;
    } else {
      // Open modal with list
      setSelectedAccountForTagBased(accountNumber);
      setTagBasedSchedules(tagBasedSchedules);
      setTagBasedModalOpen(true);
    }
  };

  // Handle recurring click
  const handleRecurringClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForRecurring(accountNumber);
      setRecurringSchedules(schedules);
      setRecurringModalOpen(true);
    }
  };

  // Handle on demand click
  const handleOnDemandClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForOnDemand(accountNumber);
      setOnDemandSchedules(schedules);
      setOnDemandModalOpen(true);
    }
  };

  // Handle WAFR click
  const handleWafrClick = (accountNumber, schedules) => {
    if (schedules.length === 1) {
      return;
    } else {
      setSelectedAccountForWafr(accountNumber);
      setWafrSchedules(schedules);
      setWafrModalOpen(true);
    }
  };

  // Helper function to format large numbers (only format very large numbers to preserve default appearance)
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 10000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    // For numbers < 10000, show full number to maintain good default appearance
    return num.toString();
  };

  // Helper function to render schedule modal
  const renderScheduleModal = (open, onClose, title, schedules, accountNumber) => {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "1.125rem",
            pb: 1,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          {title} - {accountNumber || "N/A"}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#fafafa" }}>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      color: "#374151",
                      fontSize: "0.75rem",
                      py: 1,
                    }}
                  >
                    Schedule Name
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 600,
                      color: "#374151",
                      fontSize: "0.75rem",
                      py: 1,
                      width: "120px",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule, index) => {
                  const frameworkCount = schedule.framework_count ?? 0;
                  const suppressionCount = schedule.suppression_count ?? 0;
                  const formattedFrameworkCount = formatNumber(frameworkCount);
                  const formattedSuppressionCount = formatNumber(suppressionCount);
                  return (
                    <TableRow
                      key={schedule.schedule_id || index}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#fafafa",
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#374151",
                            fontSize: "0.75rem",
                          }}
                        >
                          {formatScheduleName(schedule)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                          <Tooltip title={`Frameworks: ${frameworkCount.toLocaleString()}`}>
                            <Chip
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <span>{formattedFrameworkCount}</span>
                                  <EditIcon sx={{ fontSize: 14, color: frameworkCount > 0 ? "#2e7d32" : "#9e9e9e" }} />
                                </Box>
                              }
                              size="small"
                              onClick={() => {
                                handleViewSchedule(schedule);
                                onClose();
                              }}
                              sx={{
                                height: 24,
                                minWidth: "auto",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                backgroundColor: frameworkCount > 0 ? "#e8f5e9" : "#f5f5f5",
                                color: frameworkCount > 0 ? "#2e7d32" : "#9e9e9e",
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: frameworkCount > 0 ? "#c8e6c9" : "#e0e0e0",
                                },
                                "& .MuiChip-label": {
                                  paddingLeft: "6px",
                                  paddingRight: "6px",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </Tooltip>
                          <Tooltip title={`View Suppressions: ${suppressionCount.toLocaleString()}`}>
                            <Chip
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <span>{formattedSuppressionCount}</span>
                                  <VisibilityIcon sx={{ fontSize: 14, color: suppressionCount > 0 ? "#1976d2" : "#9e9e9e" }} />
                                </Box>
                              }
                              size="small"
                              onClick={() => {
                                handleViewSuppressions(schedule);
                                onClose();
                              }}
                              sx={{
                                height: 24,
                                minWidth: "auto",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                backgroundColor: suppressionCount > 0 ? "#e3f2fd" : "#f5f5f5",
                                color: suppressionCount > 0 ? "#1976d2" : "#9e9e9e",
                                cursor: "pointer",
                                "&:hover": {
                                  backgroundColor: suppressionCount > 0 ? "#bbdefb" : "#e0e0e0",
                                },
                                "& .MuiChip-label": {
                                  paddingLeft: "6px",
                                  paddingRight: "6px",
                                  whiteSpace: "nowrap",
                                },
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    );
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get pagination info
  const totalPages = groupedSchedulesByAccount?.total
    ? Math.ceil(groupedSchedulesByAccount.total / pageSize)
    : 0;
  const currentPageStart = groupedSchedulesByAccount?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = groupedSchedulesByAccount?.items?.length
    ? Math.min(page * pageSize, groupedSchedulesByAccount.total || 0)
    : 0;

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mt: "2rem",
        mb: "3rem",
        backgroundColor: "#ffffff",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "#374151",
            fontSize: "1rem",
            letterSpacing: "-0.01em",
            mb: 0.25,
          }}
        >
          Compliance Controls/Checks Suppression
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            fontSize: "0.8125rem",
            fontWeight: 400,
          }}
        >
          Manage Compliance Control/Checks Suppression
        </Typography>
      </Box>

      {/* Loading State */}
      {isFetchingSchedules && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
          <Typography
            variant="body2"
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading schedules...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchSchedulesError && !isFetchingSchedules && (
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #f87171",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchSchedulesError)}
          </Typography>
        </Box>
      )}

      {/* Schedules Table */}
      {allSchedules && !isFetchingSchedules && !fetchSchedulesError && (
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            backgroundColor: "#ffffff",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                }}
              >
                Schedules
              </Typography>
              {/* Filters and Search in Header */}
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1); // Reset to first page when search changes
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 250,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.8125rem",
                      backgroundColor: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "6px",
                      "&:hover": {
                        borderColor: "#d1d5db",
                      },
                      "&.Mui-focused": {
                        borderColor: "#3b82f6",
                        boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.1)",
                      },
                      "& fieldset": {
                        border: "none",
                      },
                    },
                  }}
                />
                <SuppressionFilters
                  availableAccountNumbers={availableAccountNumbers}
                  selectedAccountNumber={selectedAccountNumber}
                  onAccountChange={handleAccountChange}
                  isAzureView={isAzureView}
                />
              </Box>
            </Box>

            <TableContainer
              sx={{
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      {accountLabels.numberHeader}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Recurring
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      On Demand
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <Tooltip title="Well Architected Framework Review" arrow>
                        <span>WAFR</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1.5,
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      Tag Based
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedSchedulesByAccount?.items && groupedSchedulesByAccount.items.length > 0 ? (
                    groupedSchedulesByAccount.items.map((accountData, index) => {
                      // Helper function to format large numbers (only format very large numbers to preserve default appearance)
                      const formatNumber = (num) => {
                        if (num >= 1000000) {
                          return `${(num / 1000000).toFixed(1)}M`;
                        } else if (num >= 10000) {
                          return `${(num / 1000).toFixed(1)}K`;
                        }
                        // For numbers < 10000, show full number to maintain good default appearance
                        return num.toString();
                      };

                      const renderActionIcons = (schedule) => {
                        if (!schedule) return null;
                        const suppressionCount = schedule.suppression_count ?? 0;
                        const frameworkCount = schedule.framework_count ?? 0;
                        const formattedFrameworkCount = formatNumber(frameworkCount);
                        const formattedSuppressionCount = formatNumber(suppressionCount);
                        return (
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                            <Tooltip title={`Frameworks: ${frameworkCount.toLocaleString()}`}>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <span>{formattedFrameworkCount}</span>
                                    <EditIcon sx={{ fontSize: 14, color: frameworkCount > 0 ? "#2e7d32" : "#9e9e9e" }} />
                                  </Box>
                                }
                                size="small"
                                onClick={() => handleViewSchedule(schedule)}
                                sx={{
                                  height: 24,
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  backgroundColor: frameworkCount > 0 ? "#e8f5e9" : "#f5f5f5",
                                  color: frameworkCount > 0 ? "#2e7d32" : "#9e9e9e",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: frameworkCount > 0 ? "#c8e6c9" : "#e0e0e0",
                                  },
                                  "& .MuiChip-label": {
                                    paddingLeft: "6px",
                                    paddingRight: "6px",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                              />
                            </Tooltip>
                            <Tooltip title={`View Suppressions: ${suppressionCount.toLocaleString()}`}>
                              <Chip
                                label={
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <span>{formattedSuppressionCount}</span>
                                    <VisibilityIcon sx={{ fontSize: 14, color: suppressionCount > 0 ? "#1976d2" : "#9e9e9e" }} />
                                  </Box>
                                }
                                size="small"
                                onClick={() => handleViewSuppressions(schedule)}
                                sx={{
                                  height: 24,
                                  minWidth: "auto",
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  backgroundColor: suppressionCount > 0 ? "#e3f2fd" : "#f5f5f5",
                                  color: suppressionCount > 0 ? "#1976d2" : "#9e9e9e",
                                  cursor: "pointer",
                                  "&:hover": {
                                    backgroundColor: suppressionCount > 0 ? "#bbdefb" : "#e0e0e0",
                                  },
                                  "& .MuiChip-label": {
                                    paddingLeft: "6px",
                                    paddingRight: "6px",
                                    whiteSpace: "nowrap",
                                  },
                                }}
                              />
                            </Tooltip>
                          </Box>
                        );
                      };

                      const renderScheduleCell = (schedules, onClickHandler, typeLabel = "workloads") => {
                        if (!schedules || schedules.length === 0) {
                          return <Typography variant="body2" sx={{ color: "#9ca3af", fontSize: "0.75rem" }}>-</Typography>;
                        }
                        if (schedules.length === 1) {
                          return renderActionIcons(schedules[0]);
                        } else {
                          return (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => onClickHandler(accountData.account_number, schedules)}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.75rem",
                                  minWidth: "auto",
                                  px: 1.5,
                                  py: 0.5,
                                }}
                              >
                                {schedules.length} {typeLabel}
                              </Button>
                            </Box>
                          );
                        }
                      };

                      const renderTagBasedCell = () => {
                        const tagBased = accountData.tagBased || [];
                        return renderScheduleCell(tagBased, handleTagBasedClick, "workloads");
                      };

                      return (
                        <TableRow
                          key={accountData.account_number || index}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#fafafa",
                            },
                            borderBottom: "1px solid #f3f4f6",
                          }}
                        >
                          <TableCell sx={{ py: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#374151",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                                wordBreak: "break-all",
                              }}
                            >
                              {accountData.account_number || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderScheduleCell(accountData.recurring || [], handleRecurringClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderScheduleCell(accountData.onDemand || [], handleOnDemandClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderScheduleCell(accountData.wafr || [], handleWafrClick, "schedules")}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 1 }}>
                            {renderTagBasedCell()}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                          }}
                        >
                          No schedules found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                minHeight: 40,
                gap: 2,
              }}
            >
              {/* Badge Legend */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: "20px",
                      height: "3px",
                      backgroundColor: "#2e7d32",
                      borderRadius: "2px",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontSize: "0.7rem" }}
                  >
                    Frameworks Count
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box
                    sx={{
                      width: "20px",
                      height: "3px",
                      backgroundColor: "#1976d2",
                      borderRadius: "2px",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: "#6b7280", fontSize: "0.7rem" }}
                  >
                    Suppression Count
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: "auto" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", fontSize: "0.75rem" }}
                >
                  Rows per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      fontSize: "0.75rem",
                      height: "28px",
                      "& .MuiSelect-select": {
                        py: 0.5,
                        px: 1,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Page {page} of {totalPages || 1}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <FirstPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <LastPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Tag Based Modal */}
      {renderScheduleModal(
        tagBasedModalOpen,
        () => {
          setTagBasedModalOpen(false);
          setSelectedAccountForTagBased(null);
          setTagBasedSchedules([]);
        },
        "Tag Based Workloads",
        tagBasedSchedules,
        selectedAccountForTagBased
      )}

      {/* Recurring Modal */}
      {renderScheduleModal(
        recurringModalOpen,
        () => {
          setRecurringModalOpen(false);
          setSelectedAccountForRecurring(null);
          setRecurringSchedules([]);
        },
        "Recurring Schedules",
        recurringSchedules,
        selectedAccountForRecurring
      )}

      {/* On Demand Modal */}
      {renderScheduleModal(
        onDemandModalOpen,
        () => {
          setOnDemandModalOpen(false);
          setSelectedAccountForOnDemand(null);
          setOnDemandSchedules([]);
        },
        "On Demand Schedules",
        onDemandSchedules,
        selectedAccountForOnDemand
      )}

      {/* WAFR Modal */}
      {renderScheduleModal(
        wafrModalOpen,
        () => {
          setWafrModalOpen(false);
          setSelectedAccountForWafr(null);
          setWafrSchedules([]);
        },
        "WAFR Schedules",
        wafrSchedules,
        selectedAccountForWafr
      )}
    </Box>
  );
};

export default Suppression;

